import { AbstractControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { DateTime } from 'ts-luxon';

import { WhiteSpaceValidator } from './whitespace.validator';

export enum InlineDeviceNameValidationRules {
	MaxLength = 31,
	Regex = '^[a-zA-Z0-9][a-zA-Z0-9-]*$'
}

export enum RoomNameValidationRules {
	MaxLength = 64,
	Regex = '^[a-zA-Z0-9 _()]*$'
}

export enum RoomPasswordValidationRules {
	MinLength = 8,
	MaxLength = 64
}

export enum RoomDescriptionValidationRules {
	MaxLength = 125
}

export function getDeviceNameValidators(): ValidatorFn[] {
	return [
		Validators.required,
		Validators.maxLength(InlineDeviceNameValidationRules.MaxLength),
		Validators.pattern(InlineDeviceNameValidationRules.Regex)
	];
}

export function getRoomNameValidators(): ValidatorFn[] {
	return [
		Validators.required,
		Validators.maxLength(RoomNameValidationRules.MaxLength),
		Validators.pattern(RoomNameValidationRules.Regex),
		WhiteSpaceValidator.valueHasConsecutiveSpaces,
		WhiteSpaceValidator.valueHasSpaceAtFirstOrLastCharacter
	];
}

export function getRoomPasswordValidators(): ValidatorFn[] {
	return [
		Validators.required,
		Validators.minLength(RoomPasswordValidationRules.MinLength),
		Validators.maxLength(RoomPasswordValidationRules.MaxLength)
	];
}

export function getRoomDescriptionValidators(): ValidatorFn[] {
	return [Validators.maxLength(RoomDescriptionValidationRules.MaxLength)];
}

export function getNumberValidator(): ValidatorFn {
	return (control: AbstractControl): ValidationErrors | null => {
		const value = control.value;

		if (!value) {
			return null;
		}

		if (isNaN(value)) {
			return <ValidationErrors>{ numberinvalid: true };
		}

		return null;
	};
}

export function numberInRangeValidator(range: { min: number; max: number }): ValidatorFn {
	return (control: AbstractControl): ValidationErrors | null => {
		const value = control.value;

		if (value === null || (value >= range.min && value <= range.max)) {
			return null;
		}

		return <ValidationErrors>{ outsideRange: true };
	};
}

/**
 * Validator function for checking the validity of a license group name.
 * Ensures that the name starts with a letter or number and contains only letters,
 * numbers, spaces, underscores, and hyphens.
 * Handles empty string case specifically for desired error messages.
 */
export function licenseGroupNameValidator(): ValidatorFn {
	return (control: AbstractControl): { [key: string]: unknown } | null => {
		const value = control.value;
		const errors: { [key: string]: boolean } = {};

		if (value === null || value === undefined || value === '') {
			// eslint-disable-next-line dot-notation
			errors['required'] = true;
			// eslint-disable-next-line dot-notation
			errors['invalidStart'] = true;
			return Object.keys(errors).length ? errors : null;
		}

		const startsWithLetterOrNumber = /^[\p{L}0-9]/u.test(value);

		const allowedCharactersRegEx = /^[\p{L}0-9_\s-]*$/u;

		// Check if it starts with whitespace for the 'whiteSpace' error
		const startsWithWhiteSpace = /^\s/.test(value);

		if (startsWithWhiteSpace) {
			// eslint-disable-next-line dot-notation
			errors['whiteSpace'] = true;
		}

		if (!startsWithLetterOrNumber) {
			// eslint-disable-next-line dot-notation
			errors['invalidStart'] = true;
		}

		// Check if the string contains *any* characters not in the allowed set.
		// If the string contains any character NOT matching `[0-9\p{L}_\s-]`, it's invalid.
		if (!allowedCharactersRegEx.test(value)) {
			// eslint-disable-next-line dot-notation
			errors['invalidCharacters'] = true;
		}

		// Return null if there are no errors, otherwise return the errors object
		return Object.keys(errors).length ? errors : null;
	};
}

/**
 * Validator function for checking the validity of a name.
 * Supported characters including:
 * - Alphabetic characters from various languages
 * - Special characters: acute (é, á), grave (è, à), umlaut (ö, ü), circumflex (ê, ô),
 *   tilde (ñ, ã), cedilla (ç), caron (č), ligatures (Æ), dot above (Ż), slash through (ø)
 * - Hyphens (-), apostrophes ('), underscores (_), and spaces
 * - Length constraints: 1-50 characters
 */
export function nameValidator(): ValidatorFn {
	return (control: AbstractControl): { [key: string]: unknown } | null => {
		const value = control.value;
		if (!value || value === '') {
			return null;
		}

		// Length validation: 1-40 characters
		if (value.length < 1) {
			return { minLengthError: true };
		}
		if (value.length > 40) {
			return { maxLengthError: true };
		}

		if (value?.toString().trim() === '') {
			return { required: true };
		}

		// Enhanced pattern for allowed characters including:
		// \p{L} - All Unicode letters (includes diacritics: é, á, è, à, ö, ü, ê, ô, ñ, ã, ç, č, Æ, Ż, ø)
		// \p{M} - Combining marks (additional diacritics)
		// Additional characters: spaces, underscores, hyphens, apostrophes
		// Numbers are NOT allowed, prevents consecutive special characters
		const allowedPattern = /^(?!.*[\s_'-]{2})[\p{L}\p{M}\s_'-]+$/u;

		if (!allowedPattern.test(value)) {
			return { invalidCharacters: true };
		}

		return null;
	};
}

/**
 * Validator function for checking the validity of a name.
 * Ensures that the name starts with a letter or number and contains only letters,
 * numbers, spaces, underscores, and hyphens.
 */
export function descriptionValidator(): ValidatorFn {
	return (control: AbstractControl): { [key: string]: unknown } | null => {
		const value = control.value;
		if (!value || value === '') {
			return null;
		}

		const startsWithLetterOrNumber = /^[\p{L}\p{N}]/u.test(value); // Starts with a letter or number
		const endsWithLetterOrNumberOrSpace = /[\p{L}\p{N}\s]$/u.test(value); // Ends with a letter or number or space
		const validPattern = /^[\p{L}\p{N}\s_-]*$/u; // Allows letters, numbers, spaces, underscores and hyphens only
		const isValidFormat = validPattern.test(value);

		if (!isValidFormat) {
			return { invalidCharacters: true };
		}
		if (!startsWithLetterOrNumber) {
			return { invalidStart: true };
		}
		if (!endsWithLetterOrNumberOrSpace) {
			return { invalidEnd: true };
		}

		return null;
	};
}

/**
 * Validator function for checking the end of a name.
 * Ensures that the name end with a letter or number
 */
export function endsWithValidator(): ValidatorFn {
	return (control: AbstractControl): { [key: string]: unknown } | null => {
		const value = control.value;
		if (!value || value === '') {
			return null;
		}
		const endsWithLetterOrNumberOrSpace = /[\p{L}\p{N}]$/u.test(value); // Ends with a letter or number

		if (!endsWithLetterOrNumberOrSpace) {
			return { invalidEnd: true };
		}

		return null;
	};
}

/**
 * Validator function for checking the validity of a user group name.
 * Ensures that the name starts/ends with a letter or number and contains only letters,
 * numbers, spaces, underscores, and hyphens.
 */
export function userGroupNameValidator(): ValidatorFn {
	return (control: AbstractControl): { [key: string]: unknown } | null => {
		const value = String(control.value ?? '');
		if (value === '') {
			return null;
		}
		const startsWithLetterOrNumber = /^[\p{L}\p{N}]/u.test(value);
		const endsWithLetterOrNumber = /[\p{L}\p{N}]$/u.test(value);
		const validPattern = /^[\p{L}\p{N}\s_-]*$/u;
		const isValidFormat = validPattern.test(value);

		if (!startsWithLetterOrNumber) {
			return { invalidStart: true };
		}
		if (!endsWithLetterOrNumber) {
			return { invalidEnd: true };
		}
		if (value.length > 31) {
			return { invalidLength: true };
		}
		if (!isValidFormat) {
			return { invalidCharacters: true };
		}
		return null;
	};
}

/**
 * Validator function for checking the validity of domain names.
 * Ensures that the domain name contains only letters, hyphens, numbers
 * and 2 or 3 characters of top level domain.
 */
// TODO: TPD The below method needs to be uncommented once the validation pattern is confirmed.
// export function domainNameValidator() {
// 	return (control: AbstractControl): { [key: string]: unknown } | null => {
// 		// Regular expression to validate domain names
// 		const domainRegex = /^[a-zA-Z0-9-]+\.[a-zA-Z]{2,3}$/;

// 		// Split the input string by commas
// 		const domainNames = control.value.split(',');

// 		// Validate each domain name
// 		for (const domain of domainNames) {
// 			if (!domainRegex.test(domain)) {
// 				return { invalidCharacters: true }; // If any domain is invalid
// 			}
// 		}
// 		return null;
// 	};
// }

// exporting the regex so it can be used outside the context of a Validator
export const macAddressRegEx = /^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/;
export function getMacAddresValidators(): ValidatorFn[] {
	return [Validators.required, Validators.pattern(macAddressRegEx)];
}

// exporting the regex so it can be used outside the context of a Validator
export const serialNumberRegEx = /^[A-Za-z0-9_]+$/;
export function getSerialNumberValidators(): ValidatorFn[] {
	return [Validators.required, Validators.pattern(serialNumberRegEx)];
}

// exporting the regex so it can be used outside the context of a Validator
export const phoneRegex = /^\+\d{1,3}-\d{3}-\d{3}-\d{4}$/;
// Custom validator function that checks if the phone number matches the regex pattern
export const phoneNumberValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
	const valid = phoneRegex.test(control.value);
	return valid ? null : { invalidPhoneNumber: true };
};

/**
 * Validator function for checking the validity of a mobile phone number.
 * Ensures that the number starts with '+' and contains only digits.
 */
export function mobilePhoneValidator(control: AbstractControl): ValidationErrors | null {
	const value = control.value?.trim(); // Remove leading/trailing spaces

	if (!value) {
		return null; // Mobile phone is required
	}

	// Remove hyphens and other non-numeric characters except '+'
	const rawValue = value.replace(/[^+\d]/g, '');
	if (rawValue === '+') {
		return { invalidMobileNumber: true };
	}

	// Check if the number starts with '+' and contains only digits and '+'
	if (!rawValue.startsWith('+')) {
		return { missingCountryCode: true }; // Missing country code
	}

	// Check if the number contains only digits and starts with '+'
	if (!/^\+\d+$/.test(rawValue)) {
		return { invalidCharacters: true }; // Invalid characters (not digits or '+')
	}

	// Extract digits after the '+' sign
	const digitsOnly = rawValue.replace('+', ''); // Remove '+' for length validation

	// Check country code length and validate the phone number length after the country code
	const countryCode = digitsOnly.substring(0, digitsOnly.length - 10); // Country code
	const phoneNumber = digitsOnly.substring(digitsOnly.length - 10); // Phone number without country code

	if (countryCode.length < 1 || phoneNumber.length !== 10) {
		return { invalidLength: true }; // Phone number must have exactly 10 digits for country code +1
	}

	// Check if the phone number has a valid length (no more than 15 digits in total, including country code)
	if (digitsOnly.length < 11 || digitsOnly.length > 15) {
		return { invalidLength: true }; // Invalid length (must be between 11 and 15 digits including country code)
	}

	return null; // Valid phone number
}

/**
 * Validator function for password complexity.
 * Checks for:
 * - Minimum 8 characters
 * - At least 1 lowercase letter
 * - At least 1 uppercase letter
 * - At least 1 number
 * - At least 1 special character
 */
export function passwordValidator(): ValidatorFn {
	const passwordPattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{8,}$/;

	return (control: AbstractControl): ValidationErrors | null => {
		const password = control.value;

		if (!password) {
			return null;
		}

		if (!passwordPattern.test(password)) {
			return {
				passwordPattern: true
			};
		}

		return null;
	};
}

/**
 * Custom email validator for Angular reactive forms.
 *
 * - Rejects consecutive dots (e.g., "a..b@example.com")
 * - Rejects leading or trailing dots in the local part (e.g., ".user@example.com" or "user.@example.com")
 * - Allows standard email characters: a–z, 0–9, ._%+-
 * - Requires a valid domain structure (e.g., user@domain.tld)
 * - Enforces TLD length between 2 and 20 characters
 * - Enforces total email length of up to 100 characters
 *
 * Returns `{ invalidEmail: true }` when invalid, otherwise `null`.
 */
export function emailValidator(): ValidatorFn {
	return (control: AbstractControl): ValidationErrors | null => {
		const email = control.value;
		// Allow empty values here; let Validators.required handle emptiness
		if (!email) {
			return null;
		}
		// Regex: no consecutive dots, no leading/trailing dot, valid characters, TLD between 2 and 20 chars, email <= 100
		const emailRegex = /^(?=.{1,100}$)(?!.*\.\.)(?!\.)(?!.*\.$)[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,20}$/i;
		if (typeof email !== 'string' || email.length > 100 || !emailRegex.test(email)) {
			return { invalidEmail: true };
		}
		return null;
	};
}

export function dateFormatValidator(format: string): ValidatorFn {
	return (control: AbstractControl): ValidationErrors | null => {
		const value = control.value;
		if (!value) return null;
		if (value === format.toLowerCase()) return { required: true };

		const parsed = DateTime.fromFormat(value, format);
		return parsed.isValid ? null : { invalidDateFormat: true };
	};
}

/**
 * Validates that the warranty purchase date is within ±60 days
 * of the hardware purchase date.
 */
export function extendedWarrantyDateValidator(hardwareCtrl: string, warrantyCtrl: string, format: string): ValidatorFn {
	return (group: AbstractControl): ValidationErrors | null => {
		const hardwareVal = group.get(hardwareCtrl)?.value;
		const warrantyVal = group.get(warrantyCtrl)?.value;

		if (!hardwareVal || !warrantyVal) return null;

		const hardwareDate = DateTime.fromFormat(hardwareVal, format);
		const warrantyDate = DateTime.fromFormat(warrantyVal, format);

		if (!hardwareDate.isValid || !warrantyDate.isValid) return null;

		const diffInDays = warrantyDate.diff(hardwareDate, 'days').days;

		const warrantyCtrlRef = group.get(warrantyCtrl);

		if (Math.abs(diffInDays) > 60) {
			warrantyCtrlRef?.setErrors({
				...warrantyCtrlRef.errors,
				extendedWarrantyInvalid: true
			});
			return { extendedWarrantyInvalid: true };
		}

		// eslint-disable-next-line dot-notation
		if (warrantyCtrlRef?.errors?.['extendedWarrantyInvalid']) {
			const { extendedWarrantyInvalid, ...rest } = warrantyCtrlRef.errors;
			warrantyCtrlRef.setErrors(Object.keys(rest).length ? rest : null);
		}

		return null;
	};
}

export function intlPhoneValidator(control: AbstractControl): ValidationErrors | null {
	const value: string = control.value;

	if (!value) return null;

	const normalized = value.replace(/\s/g, '');

	// Only digits and optional +
	if (!/^\+?[0-9]*$/.test(normalized)) {
		return { invalidCharacter: true };
	}

	// + must exist at start
	if (!normalized.startsWith('+')) {
		return { invalidMobileNumber: true };
	}

	const digitsOnly = normalized.substring(1);

	if (digitsOnly.length < 7) {
		return { minlength: { requiredLength: 7 } };
	}

	if (digitsOnly.length > 15) {
		return { maxlength: { requiredLength: 15 } };
	}

	if (!/^\+[1-9][0-9]{6,14}$/.test(normalized)) {
		return { invalidMobileNumber: true };
	}

	return null;
}
